import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Trophy, ChevronDown } from 'lucide-react';
import { Spinner, ErrorBanner } from '../ReportTableKit';

const API = process.env.REACT_APP_BACKEND_URL;
const STEP = 25;
const COLS = [
  ['partner', 'Partner', 'left'], ['domain_type', 'Domain Type', 'left', false], ['region', 'Region', 'left', false],
  ['enrollments', 'Enrollments'], ['completions', 'Completions'], ['completion_rate', 'Completion Rate'], ['vouchers', 'Vouchers Issued'],
];
const MEDAL = { 1: '#d4a017', 2: '#94a3b8', 3: '#b87333' };

function Header({ sort, onSort }) {
  return (
    <thead>
      <tr>
        <th style={{ width: 64, whiteSpace: 'nowrap' }}>{sort === 'partner' ? '# (vouchers)' : '#'}</th>
        {COLS.map(([k, l, align = 'right', sortable = true]) => (
          <th key={k} style={{ textAlign: align, cursor: sortable ? 'pointer' : 'default', whiteSpace: 'nowrap', color: sort === k ? '#194449' : undefined }}
            onClick={sortable ? () => onSort(k) : undefined} data-testid={`partner-sort-${k}`}>
            {l}{sort === k && <ChevronDown size={12} style={{ verticalAlign: 'middle', marginLeft: 2 }} />}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function Row({ r, onOpen }) {
  return (
    <tr onClick={() => onOpen(r)} style={{ cursor: 'pointer' }} data-testid={`partner-row-${r.domain}`}>
      <td style={{ fontWeight: 800, color: MEDAL[r.rank] || '#475569', whiteSpace: 'nowrap' }}>
        {MEDAL[r.rank] ? <Trophy size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> : null}{r.rank}
      </td>
      <td>
        <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.partner_name}</div>
        <div style={{ fontSize: 11, color: '#64748b' }}>{r.domain}{r.partner_one_id ? ` · PartnerOne ${r.partner_one_id}` : ''}</div>
      </td>
      <td style={{ fontSize: 12 }}>{r.domain_type}</td>
      <td style={{ fontSize: 12 }}>{r.region}</td>
      <td style={{ textAlign: 'right' }}>{r.enrollments.toLocaleString()}</td>
      <td style={{ textAlign: 'right' }}>{r.completions.toLocaleString()}</td>
      <td style={{ textAlign: 'right' }}>{r.completion_rate}%</td>
      <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.vouchers.toLocaleString()}</td>
    </tr>
  );
}

function Notes({ data, sort }) {
  const u = data.unassigned || {};
  const parts = [];
  if (sort === 'completion_rate') parts.push(`Partners with fewer than ${data.min_rate_enrollments} enrollments are ranked last for completion rate.`);
  if (u.enrollments || u.vouchers) parts.push(`${(u.enrollments || 0).toLocaleString()} enrollments and ${(u.vouchers || 0).toLocaleString()} vouchers belong to learners without a registered profile/domain and aren't listed.`);
  if (!parts.length) return null;
  return <div data-testid="partner-notes" style={{ fontSize: 11, color: '#64748b', padding: '8px 16px' }}>{parts.join(' ')}</div>;
}

export function PartnerLeaderboard({ filters, onOpen }) {
  const [sort, setSort] = useState('vouchers');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(STEP);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setQuery(q); setLimit(STEP); }, 400); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setLimit(STEP); }, [filters, sort]);

  useEffect(() => {
    let live = true;
    setBusy(true); setError(null);
    axios.get(`${API}/api/cert-prep/v2/partners`, { params: { ...filters, sort, q: query || undefined, limit }, withCredentials: true })
      .then(r => { if (live) setData(r.data); })
      .catch(() => { if (live) setError('Could not load the partner leaderboard.'); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [filters, sort, query, limit]);

  return (
    <div className="table-container" data-testid="partner-leaderboard" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
        <Trophy size={16} color="#194449" />
        <div style={{ fontWeight: 700, fontSize: 14 }}>Partner Leaderboard</div>
        <div style={{ fontSize: 12, color: '#64748b' }}>{data ? `${data.total.toLocaleString()} partners · grouped by email domain · click a partner to see learners` : ''}</div>
        <div style={{ position: 'relative', marginLeft: 'auto' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input className="filter-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search partner, domain or PartnerOne ID…"
            data-testid="partner-search" style={{ paddingLeft: 30, width: 280 }} />
        </div>
      </div>
      {error && <ErrorBanner message={error} testId="partner-error" />}
      {!data && !error && <Spinner />}
      {data && (
        <div style={{ opacity: busy ? 0.55 : 1, transition: 'opacity 0.2s' }}>
          <div className="table-scroll">
            <table data-testid="partner-table">
              <Header sort={sort} onSort={setSort} />
              <tbody>{data.data.map(r => <Row key={r.domain} r={r} onOpen={onOpen} />)}</tbody>
            </table>
          </div>
          {!data.data.length && <div className="empty-state" data-testid="partner-empty"><h3>No partners found</h3></div>}
          <Notes data={data} sort={sort} />
          {data.data.length < data.total && (
            <div style={{ padding: 12, textAlign: 'center' }}>
              <button className="btn btn-secondary" onClick={() => setLimit(l => l + STEP)} disabled={busy} data-testid="partner-show-more">
                Show more ({Math.min(STEP, data.total - data.data.length)} of {(data.total - data.data.length).toLocaleString()} remaining)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
